import { Button, Spacer } from '@odekoteam/doppio';
import { useEffect, useState } from 'react';

import { useUpdateLocationCustomerPickup } from '../LocationsTable/useUpdateLocationCustomerPickup';
import { useAppDispatch, useAppSelector } from '../../hooks/store';
import { modalSelector } from '../../state/selectors/modal';
import { hideModal } from '../../state/slices/modal';
import CancelButton from './CancelButton';
import { buttonContainer, styledConfirmationModal } from './styles';

export const CUSTOMER_PICKUP_ON_TITLE = 'Turn on customer pickup?';
// Two sentences because they answer different questions: what changes from now
// on, and what happens to the nights already planned. The sweep is conditional
// on the warehouse having customer-pickup routing switched on, and there is no
// field exposing that to this app, so the copy says "where" rather than
// promising a move that would not happen in a warehouse without it.
export const CUSTOMER_PICKUP_ON_BODY =
  'New orders for this location are placed in Customer Pick Ups instead of the Lobby.';
export const CUSTOMER_PICKUP_ON_SWEEP =
  'Where customer-pickup routing is switched on for this warehouse, its uncompleted Lobby stops for tonight and upcoming nights move into Customer Pick Ups automatically within a few moments. A stop already on tonight’s truck route stays put for now and moves at the next optimization pass.';

export const CUSTOMER_PICKUP_OFF_TITLE = 'Turn off customer pickup?';
export const CUSTOMER_PICKUP_OFF_BODY =
  'Unflagging changes future placement only. Stops already in Customer Pick Ups stay there and must be moved by hand.';

export function CustomerPickupToggleModal(): JSX.Element {
  const dispatch = useAppDispatch();
  const { variables } = useAppSelector(modalSelector);
  const locationsContextId = useAppSelector(
    (state) => state.locations.metrobi?.contextId,
  );
  const { submit, isSubmitting } = useUpdateLocationCustomerPickup();

  // Snapshot the request at mount. The dialog reads from a single shared modal
  // slice, so a later showModal would otherwise swap the target location out
  // from under a submit that is already in flight.
  const [request] = useState<{
    locationId?: string;
    businessName?: string;
    customerPickup: boolean;
    contextId?: string;
    returnFocusId?: string;
  }>(() => ({
    locationId: variables?.locationId,
    businessName: variables?.businessName,
    customerPickup: Boolean(variables?.customerPickup),
    contextId: variables?.contextId,
    returnFocusId: variables?.returnFocusId,
  }));

  // The locations table remounts on a warehouse switch and clears its context,
  // taking the row this dialog is about with it.
  useEffect(() => {
    if (request.contextId && request.contextId !== locationsContextId)
      dispatch(hideModal());
  }, [request.contextId, locationsContextId, dispatch]);

  const handleOnClickConfirm = async (): Promise<void> => {
    if (!request.locationId) {
      dispatch(hideModal());
      return;
    }
    await submit({
      locations: [
        { id: request.locationId, businessName: request.businessName },
      ],
      customerPickup: request.customerPickup,
      contextId: request.contextId,
      returnFocusId: request.returnFocusId,
    });
  };

  const title = request.customerPickup
    ? CUSTOMER_PICKUP_ON_TITLE
    : CUSTOMER_PICKUP_OFF_TITLE;

  return (
    <div
      id="customer-pickup-toggle-modal"
      data-test-id="customer-pickup-toggle-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="customer-pickup-toggle-title"
      sx={{ ...styledConfirmationModal, textAlign: 'left', maxWidth: '460px' }}
    >
      <div id="customer-pickup-toggle-title" sx={{ variant: 'text.headingSm' }}>
        {title}
      </div>
      <Spacer height="$3" />
      <div sx={{ variant: 'text.paragraphMdDefault', color: 'secondaryText' }}>
        {request.customerPickup
          ? CUSTOMER_PICKUP_ON_BODY
          : CUSTOMER_PICKUP_OFF_BODY}
      </div>
      {request.customerPickup && (
        <>
          <Spacer height="$2" />
          <div
            sx={{ variant: 'text.paragraphMdDefault', color: 'secondaryText' }}
          >
            {CUSTOMER_PICKUP_ON_SWEEP}
          </div>
        </>
      )}
      <div sx={{ ...buttonContainer, justifyContent: 'flex-start' }}>
        <Button
          variant="primary"
          id="customer-pickup-toggle-action-button"
          data-test-id="customer-pickup-toggle-action-button"
          onPress={handleOnClickConfirm}
          isDisabled={isSubmitting}
        >
          {isSubmitting
            ? 'Saving...'
            : request.customerPickup
            ? 'Turn on customer pickup'
            : 'Turn off customer pickup'}
        </Button>
        <Spacer width="$2" />
        <CancelButton
          variant="secondary"
          returnFocusId={request.returnFocusId}
        />
      </div>
    </div>
  );
}
